import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Card,
  CircularProgress,
  Typography,
  TextField,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import MuiConfirmDialog from "../../common/MuiConfirmDialog/MuiConfirmDialog";
import { useDocumentUploadContext } from "../../../context/DocumentUploadContext";
import { useUserContext } from "../../../context/UserContext";
import Grid from "@mui/material/Grid";
import useStyles from "./DocumentUpload.styles";
import { hasAnyOneBusinessEntityIDInNonGlobalRegions } from "../../../utils/utils";
import { GLOBAL, TFSB } from "../../../utils/constants";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const DocumentUpload = () => {
  const { classes } = useStyles();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedLabelName, setSelectedLabelName] = useState("");

  const {
    rows,
    addRow,
    removeRow,
    updateRow,
    attachFiles,
    removeFiles,
    uploadFiles,
    uploadAndReplaceFiles,
    downloadFile,
  } = useDocumentUploadContext();

  const { isAdmin, isGlobalWriteUser, selectedUserDetail } = useUserContext();

  const { regions } = selectedUserDetail || {};

  const hasTFSBWriteRole = hasAnyOneBusinessEntityIDInNonGlobalRegions(
    regions,
    [TFSB],
    true,
  );

  const isEnabledToModify = isAdmin || isGlobalWriteUser || hasTFSBWriteRole;

  const onFileChange = (index, e) => {
    try {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      attachFiles(index, files);
      e.target.value = "";
    } catch (err) {
      alert(err.message);
      e.target.value = "";
    }
  };

  const handleDeleteClick = (index) => {
    setSelectedRowIndex(index);
    setSelectedLabelName(rows[index]?.label ?? "N/A");
    setShowConfirmDialog(true);
  };

  const handleConfirmDeleteRow = async () => {
    if (selectedRowIndex === null) return;

    await removeRow(selectedRowIndex);
    setShowConfirmDialog(false);
    setSelectedRowIndex(null);
    setSelectedLabelName("");
  };

  const handleCancelDeleteRow = () => {
    setShowConfirmDialog(false);
    setSelectedRowIndex(null);
    setSelectedLabelName("");
  };

  return (
    <div className={classes.page}>
      <Card
        style={{
          padding: "6px",
          marginBottom: "20px",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: "#6a6a6a",
            margin: 2,
            fontWeight: "bold",
          }}
        >
          The Document Upload can only be performed by {GLOBAL?.toUpperCase()}{" "}
          users and {TFSB?.toUpperCase()} users with Write access.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 12 }}>
            {!rows || !rows.length ? (
              <Item>
                <Typography
                  variant="p"
                  component="p"
                  sx={{
                    color: "#adadad",
                    minHeight: "250px",
                  }}
                >
                  No Documents found!
                </Typography>
              </Item>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Number</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Attach File</TableCell>
                    <TableCell>Upload</TableCell>
                    <TableCell>Remove File</TableCell>
                    <TableCell>Download</TableCell>
                    <TableCell>Remove Row</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        opacity: row.uploading ? 0.6 : 1,
                        pointerEvents: row.uploading ? "none" : "auto",
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.label}
                          onChange={(e) =>
                            updateRow(index, { label: e.target.value })
                          }
                          disabled={row.isPersisted}
                          placeholder="Enter label"
                          error={!!row.labelError}
                          helperText={row.labelError}
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#fff",
                              color: "#fff",
                            },
                            "& .MuiOutlinedInput-root.Mui-disabled": {
                              backgroundColor: "#616161",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: row.isPersisted ? "#000" : undefined,
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <input
                          type="file"
                          multiple={!!row.isMultiFileSupport}
                          accept={row.accept}
                          hidden
                          id={`file-input-${index}`}
                          disabled={row.uploading || !isEnabledToModify}
                          onChange={(e) => onFileChange(index, e)}
                        />
                        <label htmlFor={`file-input-${index}`}>
                          <IconButton
                            component="span"
                            disabled={row.uploading || !isEnabledToModify}
                          >
                            <AttachFileIcon />
                          </IconButton>
                          {row.files && row.files.length > 0
                            ? row.files?.map((file) => file.name).join(", ")
                            : row.accept
                              ? `(Accepts: ${row.accept})`
                              : ""}
                        </label>
                      </TableCell>

                      <TableCell sx={{ position: "relative", minWidth: 220 }}>
                        {row.canUploadAndUpdate && (
                          <Button
                            variant="contained"
                            onClick={() => uploadFiles(index)}
                            disabled={
                              !row.files ||
                              row.files.length === 0 ||
                              row.uploading ||
                              !!row.labelError ||
                              !isEnabledToModify
                            }
                            sx={{ mr: row.canUploadAndReplace ? 1 : 0 }}
                          >
                            Upload & Append
                          </Button>
                        )}

                        {row.canUploadAndReplace && (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => uploadAndReplaceFiles(index)}
                            disabled={
                              !row.files ||
                              row.files.length === 0 ||
                              row.uploading ||
                              !!row.labelError ||
                              !isEnabledToModify
                            }
                          >
                            Upload & Replace
                          </Button>
                        )}

                        {row.uploading && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              bgcolor: "rgba(0,0,0,0.4)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 2,
                            }}
                          >
                            <CircularProgress
                              size={32}
                              sx={{ color: "white" }}
                            />
                          </Box>
                        )}
                      </TableCell>

                      <TableCell>
                        <IconButton
                          onClick={() => removeFiles(index)}
                          disabled={
                            row.uploading ||
                            !row.files?.length ||
                            !isEnabledToModify
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        {row.isDownloadable && (
                          <IconButton
                            onClick={() => downloadFile(row.label)}
                            disabled={row.uploading || !row.isPersisted}
                          >
                            <DownloadIcon />
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteClick(index)}
                          disabled={row.uploading || !isEnabledToModify}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "flex-end", margin: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={addRow}>
            Add Row
          </Button>
        </Box>
      </Card>
      <MuiConfirmDialog
        open={showConfirmDialog}
        title="Confirm Deletion"
        message={`Are you sure you want to permanently delete the label "${selectedLabelName}" and all attached files?`}
        onConfirm={handleConfirmDeleteRow}
        onCancel={handleCancelDeleteRow}
      />
    </div>
  );
};

export default DocumentUpload;
